import { cn } from '@/utils/cn';
import { Card, CardContent } from '@/components/molecules/Card';
import { Icon } from '@/components/atoms/Icon';
import styles from './MetricCard.module.scss';
import type { MetricCardProps } from './MetricCard.types';

/**
 * MetricCard - Card component for displaying metrics
 * Composition: Card + Icon + Value + Label + Optional Trend
 *
 * @example
 * <MetricCard
 *   icon={Flame}
 *   label="Day Streak"
 *   value={12}
 *   trend="+2"
 *   trendDirection="up"
 *   variant="primary"
 * />
 */
export function MetricCard({
  icon,
  label,
  value,
  description,
  trend,
  trendDirection = 'neutral',
  variant = 'default',
  className,
  ...props
}: MetricCardProps) {
  const cardClasses = cn(styles.metricCard, className);

  const iconWrapperClasses = cn(
    styles.metricCard__iconWrapper,
    styles[`metricCard__iconWrapper--${variant}`]
  );

  const trendClasses = cn(
    styles.metricCard__trend,
    styles[`metricCard__trend--${trendDirection}`]
  );

  return (
    <Card className={cardClasses} {...props}>
      <CardContent className={styles.metricCard__content}>
        <div className={iconWrapperClasses}>
          <Icon icon={icon} size="md" />
        </div>

        <div className={styles.metricCard__info}>
          <div className={styles.metricCard__valueRow}>
            <p className={styles.metricCard__value}>{value}</p>
            {trend && <span className={trendClasses}>{trend}</span>}
          </div>

          <p className={styles.metricCard__label}>{label}</p>

          {description && <p className={styles.metricCard__description}>{description}</p>}
        </div>
      </CardContent>
    </Card>
  );
}

MetricCard.displayName = 'MetricCard';
